import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Bike, Map, Wallet, Wrench, Users, ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TOUR_STEPS = [
  {
    icon: Bike,
    titleKey: 'tour.step1.title',
    descriptionKey: 'tour.step1.description',
    defaultTitle: 'Le Tue Moto',
    defaultDescription: 'Aggiungi e gestisci tutte le tue moto. Traccia i chilometri, le foto e le informazioni importanti in un unico posto.'
  },
  {
    icon: Wallet,
    titleKey: 'tour.step2.title',
    descriptionKey: 'tour.step2.description',
    defaultTitle: 'Gestione Spese',
    defaultDescription: 'Registra rifornimenti, manutenzioni e altre spese. Visualizza statistiche dettagliate sul consumo e sui costi.'
  },
  {
    icon: Wrench,
    titleKey: 'tour.step3.title',
    descriptionKey: 'tour.step3.description',
    defaultTitle: 'Manutenzione',
    defaultDescription: 'Tieni traccia di tutti gli interventi di manutenzione. Non dimenticare mai quando hai fatto l\'ultimo tagliando!'
  },
  {
    icon: Map,
    titleKey: 'tour.step4.title',
    descriptionKey: 'tour.step4.description',
    defaultTitle: 'Viaggi & Percorsi',
    defaultDescription: 'Pianifica i tuoi viaggi, salva i percorsi preferiti e trova le stazioni di servizio più vicine.'
  },
  {
    icon: Users,
    titleKey: 'tour.step5.title',
    descriptionKey: 'tour.step5.description',
    defaultTitle: 'Community',
    defaultDescription: 'Condividi i tuoi viaggi, scopri raduni e connettiti con altri motociclisti appassionati!'
  }
];

export function TourGuide({ open, onClose, onComplete }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showInitial, setShowInitial] = useState(true);

  const handleStart = () => {
    setShowInitial(false);
  };

  const handleSkip = () => {
    onComplete();
    onClose();
  };

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = TOUR_STEPS[currentStep];
  const StepIcon = step?.icon;

  if (!open) return null;

  // Initial dialog asking if user wants the tour
  if (showInitial) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-[#FF5F1F]" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-['Teko'] text-white uppercase">
              Benvenuto su Vault Ride!
            </DialogTitle>
            <DialogDescription className="text-center text-[#9CA3AF]">
              Vuoi fare un tour guidato per scoprire tutte le funzionalità dell'app?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-3 mt-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 border-[#262626] text-[#9CA3AF] hover:bg-[#262626]"
            >
              <X className="w-4 h-4 mr-2" />
              No, grazie
            </Button>
            <Button
              onClick={handleStart}
              className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
            >
              Sì, mostrami!
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Tour steps
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center">
              <StepIcon className="w-8 h-8 text-[#FF5F1F]" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl font-['Teko'] text-white uppercase">
            {t(step.titleKey, step.defaultTitle)}
          </DialogTitle>
          <DialogDescription className="text-center text-[#9CA3AF] leading-relaxed">
            {t(step.descriptionKey, step.defaultDescription)}
          </DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 my-4">
          {TOUR_STEPS.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep 
                  ? 'w-6 bg-[#FF5F1F]' 
                  : index < currentStep 
                    ? 'bg-[#FF5F1F]/50' 
                    : 'bg-[#262626]'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex gap-3 sm:gap-3">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="flex-1 border-[#262626] text-[#9CA3AF] hover:bg-[#262626] disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <Button
            onClick={handleNext}
            className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
          >
            {currentStep === TOUR_STEPS.length - 1 ? 'Inizia!' : 'Avanti'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>

        <div className="text-center mt-2">
          <button 
            onClick={handleSkip}
            className="text-[#666] text-sm hover:text-[#9CA3AF] transition-colors"
          >
            Salta il tour
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
